/**
 * RidesMeService
 * The caller's own Rides surface: rider profile, driver profile, verification, consents, summary.
 * Route: rides/api/rides/me
 */
import { BaseService } from '../base/BaseService';
import { unwrapRidesResponse as unwrap } from './unwrapRidesResponse';
/** Member-facing "me" service (JWT). */
export class RidesMeService extends BaseService {
    constructor(http, config) {
        super(http, {
            servicePath: config?.servicePath ?? RidesMeService.DEFAULT_SERVICE_PATH,
            basePath: RidesMeService.BASE_PATH,
            endpoints: config?.endpoints,
        });
    }
    // ---- summary ----
    /** GET api/rides/me/summary — hasRider/hasDriver, verification flags, next ride, roles. */
    async getSummary() {
        return unwrap(await this.getSummaryRaw(), 'Rides summary not found');
    }
    getSummaryRaw() {
        return this.http.get(this.buildUrl('summary'));
    }
    // ---- rider profile ----
    /** GET api/rides/me/profile — 404 until the member has registered as a rider. */
    async getProfile() {
        return unwrap(await this.getProfileRaw(), 'Rides rider profile not found');
    }
    getProfileRaw() {
        return this.http.get(this.buildUrl('profile'));
    }
    /** PUT api/rides/me/profile — create/update the rider profile (SexID + data-processing consent). */
    async upsertProfile(request) {
        return unwrap(await this.upsertProfileRaw(request), 'Failed to save rider profile');
    }
    upsertProfileRaw(request) {
        return this.http.put(this.buildUrl('profile'), request);
    }
    // ---- driver profile ----
    /** GET api/rides/me/driver — 404 until the member has registered as a driver. */
    async getDriver() {
        return unwrap(await this.getDriverRaw(), 'Rides driver profile not found');
    }
    getDriverRaw() {
        return this.http.get(this.buildUrl('driver'));
    }
    /** PUT api/rides/me/driver — create/update the driver profile (vehicle + licence). */
    async upsertDriver(request) {
        return unwrap(await this.upsertDriverRaw(request), 'Failed to save driver profile');
    }
    upsertDriverRaw(request) {
        return this.http.put(this.buildUrl('driver'), request);
    }
    /** DELETE api/rides/me/driver — deactivate the driver profile (closes open listings). */
    async removeDriver() {
        const response = await this.removeDriverRaw();
        return response.data ?? response.success;
    }
    removeDriverRaw() {
        return this.http.delete(this.buildUrl('driver'));
    }
    // ---- verification ----
    /** GET api/rides/me/verification — the caller's verifications (ID, licence, guardian ID). */
    async getVerifications() {
        return (await this.getVerificationsRaw()).data ?? [];
    }
    getVerificationsRaw() {
        return this.http.get(this.buildUrl('verification'));
    }
    /**
     * POST api/rides/me/verification/{typeId}/start — runs the identity rule engine on the document
     * body; the number is hashed and discarded. `typeId` = RIDES_VERIFICATION_TYPE.
     */
    async startVerification(typeId, request) {
        return unwrap(await this.startVerificationRaw(typeId, request), 'Failed to start verification');
    }
    startVerificationRaw(typeId, request) {
        return this.http.post(this.buildUrl(`verification/${typeId}/start`), request);
    }
    // ---- consents ----
    /** GET api/rides/me/consents — consent ledger (data processing, guardian). */
    async getConsents() {
        return (await this.getConsentsRaw()).data ?? [];
    }
    getConsentsRaw() {
        return this.http.get(this.buildUrl('consents'));
    }
    // ---- documents (254g uploads) ----
    /** GET api/rides/me/documents — types asked of me, my uploads, and what is still missing. */
    async getDocuments() {
        return unwrap(await this.getDocumentsRaw(), 'Rides documents not found');
    }
    getDocumentsRaw() {
        return this.http.get(this.buildUrl('documents'));
    }
    /**
     * POST api/rides/me/documents/{uploadTypeId}/upload-url — a one-time signed PUT to PRIVATE storage.
     * PUT the file to `uploadUrl` with exactly `headers` (no Authorization header), then call
     * confirmDocument. See uploadDocument for the whole flow.
     */
    async createDocumentUpload(uploadTypeId, request) {
        return unwrap(await this.createDocumentUploadRaw(uploadTypeId, request), 'Failed to start the upload');
    }
    createDocumentUploadRaw(uploadTypeId, request) {
        return this.http.post(this.buildUrl(`documents/${uploadTypeId}/upload-url`), request);
    }
    /** POST api/rides/me/documents/{rideUploadId}/confirm — the server checks the stored file and submits it. */
    async confirmDocument(rideUploadId) {
        return unwrap(await this.confirmDocumentRaw(rideUploadId), 'Failed to confirm the upload');
    }
    confirmDocumentRaw(rideUploadId) {
        return this.http.post(this.buildUrl(`documents/${rideUploadId}/confirm`), {});
    }
    /** DELETE api/rides/me/documents/{rideUploadId} — withdraw a document and delete its file. */
    async deleteDocument(rideUploadId) {
        const response = await this.deleteDocumentRaw(rideUploadId);
        return response.data ?? response.success;
    }
    deleteDocumentRaw(rideUploadId) {
        return this.http.delete(this.buildUrl(`documents/${rideUploadId}`));
    }
    /** GET api/rides/me/documents/{rideUploadId}/view — short-lived link to my own document. */
    async getDocumentLink(rideUploadId) {
        return unwrap(await this.getDocumentLinkRaw(rideUploadId), 'Document not available');
    }
    getDocumentLinkRaw(rideUploadId) {
        return this.http.get(this.buildUrl(`documents/${rideUploadId}/view`));
    }
    /**
     * Whole upload flow: upload-url → PUT the blob to private storage → confirm.
     * `blob` should already be resized/re-encoded on the device for photos (strips EXIF/GPS).
     */
    async uploadDocument(uploadTypeId, blob, options) {
        const ticket = await this.createDocumentUpload(uploadTypeId, {
            contentType: options.contentType,
            sizeBytes: blob.size,
            expiresAt: options.expiresAt ?? null,
            consentGranted: options.consentGranted ?? null,
        });
        const put = await fetch(ticket.uploadUrl, { method: ticket.method || 'PUT', headers: ticket.headers, body: blob });
        if (!put.ok) {
            throw new Error(`Document upload failed (${put.status})`);
        }
        return this.confirmDocument(ticket.rideUploadID);
    }
}
RidesMeService.DEFAULT_SERVICE_PATH = 'rides';
RidesMeService.BASE_PATH = 'api/rides/me';
//# sourceMappingURL=RidesMeService.js.map